import { useQuery, useQueryClient } from '@tanstack/react-query';
import { sendTrackSiteSearchEvent } from '@pkgs/components/utils/tracking';
import { mapSearchStateToSearchListingsFilters, prepareSearchListingsFiltersForRequest } from '@pkgs/components/utils/mapFilterFields';
import { useMapVisibilityStore } from '@pkgs/components/providers/MapVisibilityStoreProvider';
import { mapSearchListingResults, type SearchListingsOptions, type SearchListingsResponse } from '@ev-tech/search-modules-api';

import { useAiSearch } from './useAiSearch';
import { getListingsQueryKey } from './queryKeys';
import { useUrlSearchState } from '../useUrlSearchState';
import { useAppContext } from '../useAppContext';
import { useAutoCompleteStore } from '../stores/useAutoCompleteStore';
import { useSearchStore } from '../stores/search/useSearchStore';
import { mapShopNamesFromResults } from '../../utils/propertyDataUtils';

export const RESULTS_PLACEHOLDER: Readonly<SearchListingsResponse> = {
  listings: [],
  widerAreaListings: [],
  listingsTotal: 0,
};

export const useListings = () => {
  const queryClient = useQueryClient();
  const {
    api,
    tracking,
    events,
    countryCode,
    options: appOptions,
    initialFilters,
    initialResults,
    initialSortingOptionId,
    smartSortPreferences,
  } = useAppContext();
  const { isSetStateFromUrlDone } = useUrlSearchState();
  const { isPending: isAiSearchPending } = useAiSearch();
  const filterState = useSearchStore((state) => state);
  const page = useSearchStore((state) => state.page);
  const pageSize = useSearchStore((state) => state.pageSize);
  const placeName = useSearchStore((state) => state.placeName);
  const filters = mapSearchStateToSearchListingsFilters(filterState);
  const requestFilters = prepareSearchListingsFiltersForRequest({
    filters,
    sortingOptionId: filterState.sortingOptionId,
    smartSortPreferences,
  });
  const inputText = useAutoCompleteStore((state) => state.inputText);
  const typedInText = useAutoCompleteStore((state) => state.typedInText);
  const numberOfAutoCompleteOptions = useAutoCompleteStore((state) => state.numberOfAutoCompleteOptions);
  const autoCompleteOptionIndex = useAutoCompleteStore((state) => state.autoCompleteOptionIndex);
  const autoCompleteOptionCategory = useAutoCompleteStore((state) => state.autoCompleteOptionCategory);
  const searchMode = useSearchStore((state) => state.searchMode);
  const isMapToggledOn = useMapVisibilityStore((state) => state.isMapActive);
  const selectedLokaliseOptionIndex = useAutoCompleteStore((state) => state.selectedLokaliseOptionIndex);

  const queryKey = getListingsQueryKey({
    filters: requestFilters,
    options: {
      ...appOptions,
      pageSize,
      page,
    },
  });

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const options: SearchListingsOptions = { ...appOptions, page, pageSize };

      const propertiesResult = await api.searchListings(requestFilters, options);

      if (propertiesResult.status === 'error') {
        throw propertiesResult.error;
      }

      const listings = mapSearchListingResults(propertiesResult.data.listings);

      const shopNames = mapShopNamesFromResults(listings);

      sendTrackSiteSearchEvent({
        results: propertiesResult.data,
        filters,
        options,
        shopNames,
        placeName,
        inputText,
        typedInText,
        numberOfAutoCompleteOptions,
        autoCompleteOptionIndex,
        tracking,
        countryCode,
        isMapToggledOn,
        autoCompleteOptionCategory,
        searchMode,
        selectedLokaliseOptionIndex,
        sortingOptionId: filterState.sortingOptionId,
      });

      if (events?.onResultsChange) events.onResultsChange(listings);

      return propertiesResult.data;
    },
    placeholderData: (previousData) => {
      if (previousData) return previousData;

      if (!initialFilters) return undefined;

      if (initialResults) return initialResults;

      return queryClient.getQueryData<SearchListingsResponse | undefined>(
        getListingsQueryKey({
          filters: prepareSearchListingsFiltersForRequest({
            filters: initialFilters,
            sortingOptionId: initialSortingOptionId,
            smartSortPreferences,
          }),
          options: {
            ...appOptions,
            page,
            pageSize,
          },
        }),
      );
    },
    enabled: isSetStateFromUrlDone && !isAiSearchPending,
  });

  return {
    ...query,
    // Reason: the query result does not expose queryKey.
    queryKey,
    data: query.data ?? RESULTS_PLACEHOLDER,
  };
};
